import React from 'react';

import type { ConfirmDialogProps } from '../components/ConfirmDialog';

/**
 * Open/close logic for the ConfirmDialog that returns a promise.
 * It resolves to `true` on confirm and `false` on cancel.
 */
export function usePromiseConfirmDialog() {
    const [confirmDialogProps, setConfirmDialogProps] = React.useState<ConfirmDialogProps | null>(null);

    const close = React.useCallback(
        () =>
            setConfirmDialogProps((prev) => {
                if (!prev) {
                    return null;
                }
                return { ...prev, isOpen: false };
            }),
        [],
    );

    const open = React.useCallback(
        (props: Omit<ConfirmDialogProps, 'onConfirm' | 'onClose'>) =>
            new Promise<boolean>((resolve) => {
                setConfirmDialogProps({
                    ...props,
                    isOpen: true,
                    onConfirm: () => {
                        resolve(true);
                        close();
                    },
                    onClose: () => {
                        resolve(false);
                        close();
                    },
                } as ConfirmDialogProps);
            }),
        [close],
    );

    return { open, close, props: confirmDialogProps };
}
